import React from 'react';

export const money = (value, currency = 'CLP') => {
  const code = String(currency || 'CLP').toUpperCase();
  try {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: code === 'CLP' ? 0 : 2,
    }).format(Number(value || 0));
  } catch {
    return `${code} ${Number(value || 0).toLocaleString('es-CL')}`;
  }
};

export const formatDate = (value) => {
  const raw = String(value || '').slice(0, 10);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : raw;
};

export const accountLabel = (row) =>
  [row?.bank_name, row?.account_name].filter(Boolean).join(' · ') || 'Sin cuenta';

export const Stacked = ({ top, bottom, topColor, bottomColor, muted }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div style={{ fontWeight: 700, fontSize: 12, color: topColor }}>{top || '—'}</div>
    {bottom ? (
      <div style={{ fontSize: 11, color: bottomColor || muted, marginTop: 1 }}>{bottom}</div>
    ) : null}
  </div>
);

const PILL_TONES = {
  success: {
    light: { bg: '#dcfce7', color: '#166534', border: '#86efac' },
    dark: { bg: '#14532d', color: '#bbf7d0', border: '#22c55e' },
  },
  info: {
    light: { bg: '#e0f2fe', color: '#0369a1', border: '#7dd3fc' },
    dark: { bg: '#1e3a5f', color: '#7dd3fc', border: '#0284c7' },
  },
  warning: {
    light: { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' },
    dark: { bg: '#451a03', color: '#fcd34d', border: '#b45309' },
  },
  purple: {
    light: { bg: '#f3e8ff', color: '#6b21a8', border: '#d8b4fe' },
    dark: { bg: '#3b0764', color: '#e9d5ff', border: '#a855f7' },
  },
  danger: {
    light: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
    dark: { bg: '#7f1d1d', color: '#fecaca', border: '#ef4444' },
  },
};

export const Pill = ({ label, tone = 'info', isDarkMode = false }) => {
  const palette = (PILL_TONES[tone] || PILL_TONES.info)[isDarkMode ? 'dark' : 'light'];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 9px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 650,
        color: palette.color,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};

export const downloadCsv = (filename, headers, rows) => {
  const escape = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const lines = [headers, ...rows].map((line) => line.map(escape).join(';'));
  const blob = new Blob([`\ufeff${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const buildFormStyles = (ui) => ({
  input: {
    height: 34,
    borderRadius: 8,
    border: `1px solid ${ui.inputBorder}`,
    background: ui.inputBg,
    color: ui.inputText,
    padding: '0 10px',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box',
  },
  label: { display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary },
  primaryBtn: {
    height: 34,
    borderRadius: 8,
    border: 'none',
    background: ui.interactive,
    color: ui.textOnAccent || '#fff',
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  ghostBtn: {
    height: 34,
    borderRadius: 8,
    border: `1px solid ${ui.border}`,
    background: 'transparent',
    color: ui.text,
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: 12,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  sectionTitle: {
    margin: '6px 0 0',
    fontSize: 11,
    fontWeight: 700,
    color: ui.muted,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
});

/** Panel lateral derecho con el mismo formato que el formulario del Libro de ventas. */
export const TreasuryDrawer = ({
  ui,
  title,
  subtitle,
  children,
  error = '',
  saving = false,
  onClose,
  onSave,
  saveLabel = 'Guardar',
}) => {
  const styles = buildFormStyles(ui);
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: ui.overlay || 'rgba(0,0,0,0.45)',
        zIndex: 10000,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 480,
          maxWidth: '100%',
          height: '100%',
          background: ui.panel,
          borderLeft: `1px solid ${ui.border}`,
          padding: 20,
          boxSizing: 'border-box',
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          gap: 16,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>{title}</p>
          {subtitle ? <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>{subtitle}</p> : null}
        </div>
        <div style={{ overflowY: 'auto', display: 'grid', gap: 10, alignContent: 'start' }}>
          {children}
          {error ? <p style={{ margin: 0, color: ui.danger, fontSize: 12 }}>{error}</p> : null}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" style={styles.ghostBtn} onClick={onClose}>
            {onSave ? 'Cancelar' : 'Cerrar'}
          </button>
          {onSave ? (
            <button type="button" style={styles.primaryBtn} onClick={onSave} disabled={saving}>
              {saving ? 'Guardando...' : saveLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
